/**
 * FiltriPanel — F-006 Phase 5 (IMPL-P5-005).
 *
 * Sidebar filter panel that reads/writes the URL search params via
 * `useArchivio()`. Filters:
 *
 *  - tipologia (testo libero)
 *  - stato     (testo libero — F-005 stati pratica)
 *  - parte     ('proprietario' | 'inquilino')
 *  - dateFrom  (ISO date)
 *  - dateTo    (ISO date)
 *
 * V1: simple `<select>` and `<input type="date">` — no shadcn Select to keep
 * the component lightweight. Future iterations can swap in a richer UI.
 */
import type { ChangeEvent } from 'react';
import clsx from 'clsx';
import { useArchivio } from '../hooks/useArchivio';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface FiltriPanelProps {
  className?: string;
}

const PARTE_OPTIONS = [
  { value: '', label: 'Tutte' },
  { value: 'proprietario', label: 'Proprietario' },
  { value: 'inquilino', label: 'Inquilino' },
] as const;

/**
 * SIM-021: keep ISO-date handling consistent. We always serialize the
 * `<input type="date">` value (YYYY-MM-DD) and append a fixed time-of-day
 * UTC suffix when writing back to the URL params. Reads slice the first 10
 * chars defensively — both routes share this helper so any future change to
 * the format (e.g. dropping milliseconds) lives in one place.
 */
function toDateInputValue(iso: string | undefined): string {
  if (!iso) return '';
  // The shared schema produces ISO 8601 (`YYYY-MM-DDTHH:mm:ss.sssZ`). Slicing
  // is safer than `new Date(...).toISOString()` because we don't want a
  // timezone roundtrip when the user is in a non-UTC TZ.
  return iso.slice(0, 10);
}

function fromDateInputStart(value: string): string | undefined {
  return value ? `${value}T00:00:00.000Z` : undefined;
}

function fromDateInputEnd(value: string): string | undefined {
  return value ? `${value}T23:59:59.999Z` : undefined;
}

export function FiltriPanel({ className }: FiltriPanelProps) {
  const { filters, setFilter } = useArchivio();

  const on = (key: 'tipologia' | 'stato' | 'parte' | 'dateFrom' | 'dateTo') => {
    return (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const v = e.target.value;
      setFilter(key, v || undefined);
    };
  };

  return (
    <aside
      className={clsx('space-y-4', className)}
      aria-label="Filtri archivio"
    >
      <div>
        <h2 className="mb-2 text-sm font-semibold text-foreground">Filtri</h2>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="filtro-tipologia">Tipologia</Label>
        <Input
          id="filtro-tipologia"
          type="text"
          value={filters.tipologia ?? ''}
          onChange={on('tipologia')}
          placeholder="Es. abitativo"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="filtro-stato">Stato</Label>
        <Input
          id="filtro-stato"
          type="text"
          value={filters.stato ?? ''}
          onChange={on('stato')}
          placeholder="Es. COMPLETATA"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="filtro-parte">Parte</Label>
        <select
          id="filtro-parte"
          value={filters.parte ?? ''}
          onChange={on('parte')}
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {PARTE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="filtro-date-from">Dal</Label>
        <Input
          id="filtro-date-from"
          type="date"
          value={toDateInputValue(filters.dateFrom)}
          onChange={(e) => setFilter('dateFrom', fromDateInputStart(e.target.value))}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="filtro-date-to">Al</Label>
        <Input
          id="filtro-date-to"
          type="date"
          value={toDateInputValue(filters.dateTo)}
          onChange={(e) => setFilter('dateTo', fromDateInputEnd(e.target.value))}
        />
      </div>
    </aside>
  );
}
